/* eslint-disable no-console */

export type Operator =
  | 'equals'
  | 'notEquals'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'isEmpty'
  | 'notEmpty'
  | 'in'
  | 'notIn'
  | 'true'
  | 'false';

export interface Condition {
  parentField?: string;
  field: string;
  operator: Operator;
  value?: unknown;
}

export type Expression = Condition | {and: Expression[]} | {or: Expression[]};

/**
 * Evaluates a conditional expression against the provided data object.
 * - Missing fields → undefined
 * - Safe, no external dependencies
 */
export function evaluateExpression(
  expr: Expression | null | undefined,
  data: Record<string, unknown> = {}
): boolean {
  if (expr == null) {
    return true;
  }

  if ('field' in expr && 'operator' in expr) {
    const fieldValue = expr.field in data ? data[expr.field] : undefined;
    const op = expr.operator;
    const compareTo = expr.value;

    switch (op) {
      case 'equals':
        return fieldValue === compareTo;
      case 'notEquals':
        return fieldValue !== compareTo;

      case 'greaterThan':
      case 'greaterThanOrEqual':
      case 'lessThan':
      case 'lessThanOrEqual': {
        if (fieldValue === null || typeof fieldValue !== 'number') return false;
        if (compareTo === null || typeof compareTo !== 'number') return false;
        switch (op) {
          case 'greaterThan':
            return fieldValue > compareTo;
          case 'greaterThanOrEqual':
            return fieldValue >= compareTo;
          case 'lessThan':
            return fieldValue < compareTo;
          case 'lessThanOrEqual':
            return fieldValue <= compareTo;
          default:
            return false;
        }
      }
      case 'contains': {
        if (fieldValue == null) return false;
        if (typeof fieldValue === 'string')
          return fieldValue.includes(String(compareTo ?? ''));

        if (Array.isArray(fieldValue)) return fieldValue.includes(compareTo);

        return false;
      }

      case 'notContains':
        return !evaluateExpression(
          {field: expr.field, operator: 'contains', value: compareTo},
          data
        );

      case 'startsWith':
      case 'endsWith': {
        if (typeof fieldValue !== 'string' || fieldValue == null) return false;
        const method = op === 'startsWith' ? 'startsWith' : 'endsWith';

        return fieldValue[method](String(compareTo ?? ''));
      }

      case 'isEmpty': {
        if (fieldValue === undefined || fieldValue === null) return true;
        if (typeof fieldValue === 'string') return fieldValue.trim() === '';
        if (Array.isArray(fieldValue)) return fieldValue.length === 0;
        if (fieldValue && typeof fieldValue === 'object')
          return Object.keys(fieldValue).length === 0;

        return false;
      }

      case 'notEmpty':
        return !evaluateExpression(
          {field: expr.field, operator: 'isEmpty'},
          data
        );

      case 'in':
        return Array.isArray(compareTo) && compareTo.includes(fieldValue);

      case 'notIn':
        return Array.isArray(compareTo) && !compareTo.includes(fieldValue);

      case 'true':
        return !!fieldValue;

      case 'false':
        return !fieldValue;

      // default:
      //   console.warn(`Unknown operator: ${op}`);
      //   return false;
    }
  }

  if ('and' in expr && Array.isArray(expr.and)) {
    if (expr.and.length === 0) return true;

    return expr.and.every((sub) => {
      if ('field' in sub && 'operator' in sub && sub?.parentField) {
        const itemArray: any = Array.isArray(data?.[sub?.parentField])
          ? data?.[sub?.parentField]
          : [];

        if (itemArray.length > 0) {
          return itemArray.some((item: any) => {
            return evaluateExpression(sub, item);
          });
        }

        return false;
      }

      return evaluateExpression(sub, data);
    });
  }

  if ('or' in expr && Array.isArray(expr.or)) {
    if (expr.or.length === 0) return false;

    return expr.or.some((sub) => {
      if ('field' in sub && 'operator' in sub && sub?.parentField) {
        const itemArray: any = Array.isArray(data?.[sub?.parentField])
          ? data?.[sub?.parentField]
          : [];

        if (itemArray.length > 0) {
          return itemArray.some((item: any) => {
            return evaluateExpression(sub, item);
          });
        }

        return false;
      }

      return evaluateExpression(sub, data);
    });
  }

  console.warn('Invalid expression structure', expr);

  return false;
}
